import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RefreshCw, ArrowUpDown } from "lucide-react";
import { usePoll } from "@/hooks/usePoll";
import { fmt, timeAgo } from "@/lib/api";
import { SignalBadge, TrendTag, ConfidenceBar, PageHeader } from "@/components/Badges";

const CCYS = ["ALL", "USD", "EUR", "GBP", "JPY", "AUD", "NZD", "CAD", "CHF"];
const SESS = ["ALL", "Sydney", "Tokyo", "London", "New York"];
const FILTERS = [["ALL", "All"], ["BUY", "Buy"], ["SELL", "Sell"], ["HIGH", "High conf ≥75"], ["VHIGH", "Very high ≥85"]];

const Sel = ({ value, onChange, options, testid }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
    className="bg-white border border-zinc-200 rounded-sm text-xs px-2 py-1.5 text-zinc-800 focus:outline-none focus:border-[#007AFF]">
    {options.map((o) => <option key={o} value={o}>{o}</option>)}
  </select>
);

export default function Scanner() {
  const { data, loading, reload } = usePoll("/scanner", 30000);
  const nav = useNavigate();
  const [f, setF] = useState("ALL");
  const [ccy, setCcy] = useState("ALL");
  const [sess, setSess] = useState("ALL");
  const [minRR, setMinRR] = useState("0");
  const [sort, setSort] = useState({ k: "confidence", d: -1 });
  const rows = useMemo(() => {
    let r = data?.results || [];
    if (f === "BUY" || f === "SELL") r = r.filter((x) => x.signal === f);
    if (f === "HIGH") r = r.filter((x) => x.confidence >= 75);
    if (f === "VHIGH") r = r.filter((x) => x.confidence >= 85);
    if (ccy !== "ALL") r = r.filter((x) => x.base === ccy || x.quote_ccy === ccy);
    if (sess !== "ALL") r = r.filter((x) => x.sessions.includes(sess));
    r = r.filter((x) => x.risk_reward >= Number(minRR));
    return [...r].sort((a, b) => (a[sort.k] > b[sort.k] ? 1 : -1) * sort.d);
  }, [data, f, ccy, sess, minRR, sort]);
  const H = ({ k, children }) => (
    <th onClick={() => setSort({ k, d: sort.k === k ? -sort.d : -1 })} className="px-3 py-2 text-left label-xs cursor-pointer hover:text-zinc-700 whitespace-nowrap select-none" data-testid={`sort-${k}`}>
      <span className="inline-flex items-center gap-1">{children}<ArrowUpDown size={10} /></span>
    </th>
  );
  return (
    <div data-testid="scanner-page">
      <PageHeader title="Market Scanner" sub={`${rows.length} instruments · data: ${data?.provider?.provider || "…"}`}
        right={<button onClick={reload} className="flex items-center gap-2 text-xs border border-zinc-200 px-3 py-1.5 rounded-sm hover:border-zinc-400 transition-colors" data-testid="scanner-refresh-btn"><RefreshCw size={12} className={loading ? "animate-spin" : ""} />Refresh</button>} />
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {FILTERS.map(([k, l]) => (
          <button key={k} onClick={() => setF(k)} data-testid={`filter-${k.toLowerCase()}`}
            className={`px-3 py-1.5 text-xs rounded-sm border transition-colors ${f === k ? "bg-[#007AFF] border-[#007AFF] text-white" : "border-zinc-200 text-zinc-500 hover:text-zinc-900"}`}>{l}</button>
        ))}
        <span className="label-xs ml-3">Currency</span><Sel value={ccy} onChange={setCcy} options={CCYS} testid="filter-currency" />
        <span className="label-xs ml-2">Session</span><Sel value={sess} onChange={setSess} options={SESS} testid="filter-session" />
        <span className="label-xs ml-2">Min R:R</span><Sel value={minRR} onChange={setMinRR} options={["0", "1.5", "2", "3"]} testid="filter-rr" />
        <span className="label-xs ml-2">Timeframe</span><Sel value="1H" onChange={() => {}} options={["1H"]} testid="filter-timeframe" />
      </div>
      <div className="panel overflow-x-auto scrollbar-thin">
        <table className="w-full text-sm" data-testid="scanner-table">
          <thead className="border-b border-zinc-200 bg-zinc-50">
            <tr><H k="symbol">Pair</H><H k="price">Price</H><H k="change_24h_pct">24H</H><H k="trend">Trend</H><H k="regime">Regime</H>
              <H k="signal">Signal</H><th className="px-3 py-2 text-left label-xs">Entry</th><th className="px-3 py-2 text-left label-xs">Stop</th>
              <th className="px-3 py-2 text-left label-xs">TP1</th><H k="risk_reward">R:R</H><H k="confidence">Confidence</H>
              <th className="px-3 py-2 text-left label-xs">TF</th><th className="px-3 py-2 text-left label-xs">Updated</th></tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const tradable = r.signal === "BUY" || r.signal === "SELL";
              return (
                <tr key={r.symbol} onClick={() => nav(`/signal/${r.pair_id}`)} data-testid={`scanner-row-${r.pair_id}`}
                  className="border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer transition-colors">
                  <td className="px-3 py-2.5 num font-bold text-zinc-900 whitespace-nowrap">{r.symbol}</td>
                  <td className="px-3 py-2.5 num text-zinc-800">{fmt(r.price, r.digits)}</td>
                  <td className={`px-3 py-2.5 num ${r.change_24h_pct >= 0 ? "text-[#059669]" : "text-[#DC2626]"}`}>{r.change_24h_pct >= 0 ? "+" : ""}{r.change_24h_pct.toFixed(2)}%</td>
                  <td className="px-3 py-2.5"><TrendTag trend={r.trend} /></td>
                  <td className="px-3 py-2.5 text-[11px] text-zinc-500 whitespace-nowrap">{r.regime}</td>
                  <td className="px-3 py-2.5"><SignalBadge signal={r.signal} testid={`scanner-signal-${r.pair_id}`} /></td>
                  <td className="px-3 py-2.5 num text-xs text-zinc-700 whitespace-nowrap">{tradable ? `${fmt(r.entry_zone[0], r.digits)}–${fmt(r.entry_zone[1], r.digits)}` : "—"}</td>
                  <td className="px-3 py-2.5 num text-xs text-[#DC2626]/90">{tradable ? fmt(r.stop_loss, r.digits) : "—"}</td>
                  <td className="px-3 py-2.5 num text-xs text-[#059669]/90">{tradable ? fmt(r.tp1, r.digits) : "—"}</td>
                  <td className="px-3 py-2.5 num text-xs">{tradable ? `1:${r.risk_reward}` : "—"}</td>
                  <td className="px-3 py-2.5"><ConfidenceBar value={r.confidence} testid={`scanner-confidence-${r.pair_id}`} /></td>
                  <td className="px-3 py-2.5 num text-xs text-zinc-500">{r.timeframe}</td>
                  <td className="px-3 py-2.5 num text-[11px] text-zinc-500 whitespace-nowrap">{timeAgo(data.updated_at)}</td>
                </tr>
              );
            })}
            {rows.length === 0 && <tr><td colSpan={13} className="p-8 text-center text-sm text-zinc-500" data-testid="scanner-empty">{loading ? "Scanning markets…" : "No instruments match your filters."}</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-zinc-500 mt-3">Confidence reflects the weight of available confirmations — not a probability of profit.</p>
    </div>
  );
}
